import { useDispatch } from 'react-redux';
import React, { useCallback, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { Box, Typography, LinearProgress, IconButton } from '@material-ui/core';
import { CloudUpload, Delete, InsertDriveFile, Image, PictureAsPdf } from '@material-ui/icons';

import { addPendingAttachment } from '../../store/Activities';

// Note: This component supports two modes:
// 1. Local mode (preferred): Pass onFilesAdded and onFileRemoved callbacks to manage state locally
// 2. Global mode (legacy): Uses Redux store for pendingAttachments (causes cross-component sharing issues)

const useStyles = makeStyles((theme) => ({
  dropZone: {
    border: '1px dashed #CBD6E2',
    borderRadius: theme.shape.borderRadius,
    padding: '9px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: "#EDF1FF" },
    '&.dragover': { borderColor: theme.palette.primary.main, backgroundColor: theme.palette.primary.light + '20' }
  },
  uploadIcon: { fontSize: 48, color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  fileList: { marginTop: theme.spacing(2) },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing(1),
    '&:hover': { backgroundColor: "#EDF1FF" }
  },
  fileIcon: { marginRight: theme.spacing(1), color: theme.palette.text.secondary },
  fileName: { flex: 1, marginRight: theme.spacing(1) },
  fileSize: { color: theme.palette.text.secondary, marginRight: theme.spacing(1) },
  progressBar: { marginTop: theme.spacing(1) },
  buttonContainer: { display: 'flex', justifyContent: 'flex-end' }
}));

const getFileIcon = (mimeType) => {
  if (!mimeType) return <InsertDriveFile />;
  if (mimeType.startsWith('image/')) return <Image />;
  if (mimeType === 'application/pdf') return <PictureAsPdf />;
  return <InsertDriveFile />;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const AttachmentUploader = ({ onFilesAdded, onFileRemoved, existingFiles = [], uploadProgress = {}, maxFiles = 10, maxSize = 100 * 1024 * 1024, accept = '*/*', useLocalState = false, showByDefault = false, iconOnly = false }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [dragover, setDragover] = useState(false);
  // iconOnly mode: always show the upload area directly (for edit mode)
 
  const [isExpanded, setIsExpanded] = useState(showByDefault || iconOnly || existingFiles.length > 0);

  // Auto-expand when files exist
  React.useEffect(() => {
    if (existingFiles.length > 0) {
      setIsExpanded(true);
    }
  }, [existingFiles.length]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragover(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragover(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragover(false);
  }, []);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    handleFiles(files);
  }, []);

  const [modalDroppedAlert, setModalDroppedAlert] = useState(false);
  const [invalidFilesList, setInvalidFilesList] = useState([]);

  const [maxFilesAlert, setMaxFilesAlert] = useState(false);
  const [maxFilesMessage, setMaxFilesMessage] = useState('');

  const handleFiles = (files) => {
    console.log(`[AttachmentUploader] Processing ${files.length} files...`);

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      console.log(`[AttachmentUploader] File: ${file.name}, type: ${file.type || 'unknown'}, size: ${file.size} bytes`);

      if (file.size > maxSize) {
        invalidFiles.push(`"${file.name}" is too large (${formatFileSize(file.size)}). Maximum size is ${formatFileSize(maxSize)}.`);
        return;
      }

      // Additional validation for empty files
      if (file.size === 0) {
        invalidFiles.push(`"${file.name}" is empty.`);
        return;
      }

      validFiles.push(file);
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      setInvalidFilesList(invalidFiles); // store the error messages
      setModalDroppedAlert(true);
    }

    // Check total file limit
    if (existingFiles.length + validFiles.length > maxFiles) {
      const remaining = Math.max(0, maxFiles - existingFiles.length);
      setMaxFilesMessage(`Maximum ${maxFiles} files allowed. You can add ${remaining} more files.`);
      setMaxFilesAlert(true);
      return;
    }

    // Add valid files
    const newFiles = [];
    validFiles.forEach(file => {
      const fileWithId = {
        id: Date.now() + '_' + Math.random().toString(36).substr(2, 9) + '_' + file.name,
        name: file.name,
        size: file.size,
        type: file.type || '', // Ensure type is always a string
        lastModified: file.lastModified,
        file: file // Keep the original file object for upload
      };

      console.log(`[AttachmentUploader] Adding file: ${file.name} with ID: ${fileWithId.id}`);

      // Use local state mode if callback is provided, otherwise fall back to Redux
      if (useLocalState && onFilesAdded) {
        newFiles.push(fileWithId);
      } else {
        dispatch(addPendingAttachment(fileWithId));
        if (onFilesAdded) onFilesAdded([fileWithId]);
      }
    });

    // For local state mode, call onFilesAdded once with all new files
    if (useLocalState && onFilesAdded && newFiles.length > 0) {
      onFilesAdded(newFiles);
    }

    if (validFiles.length > 0) {
      console.log(`[AttachmentUploader] Successfully added ${validFiles.length} files`);
    }
  };

  const handleRemoveFile = (fileId) => {
    // Use local state mode if callback is provided, otherwise fall back to Redux
    if (useLocalState && onFileRemoved) {
      onFileRemoved(fileId);
    } else {
      // Remove from pending attachments in Redux
      const fileToRemove = existingFiles.find(f => f['id'] === fileId);
      if (fileToRemove) {
        dispatch({ type: 'REMOVE_PENDING_ATTACHMENT', payload: fileId });
      }
    }
  };

  // Generate unique input ID to avoid conflicts when multiple uploaders are on the page
  const inputId = `file-upload-input-${React.useId ? React.useId() : Math.random().toString(36).substr(2, 9)}`;

  return (
    <>
      {/* iconOnly mode: skip button, show upload area directly (for edit mode) */}
      {!isExpanded && !iconOnly ? (
        <button
          type="button"
          className="btn-add-attachment"
          onClick={() => setIsExpanded(true)}
        >
          <CloudUpload style={{ fontSize: 18, marginRight: 8 }} />
          Add Attachment
        </button>
      ) : (
        <>
          <div
            className={`dragbox-attachment ${classes.dropZone} ${dragover ? 'dragover' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById(inputId).click()}
          >
            <div className='inner-attachment'>
              <span className='icon'>
                <CloudUpload className={classes.uploadIcon} />
                <h5>Drag and drop files here or click to browse</h5>
                <p>Maximum {maxFiles} files, up to {formatFileSize(maxSize)} each</p>
              </span>
            </div>
            <input
              id={inputId}
              type="file"
              multiple
              accept={accept}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              key={existingFiles.length} // Force re-render when files change to clear input
            />
          </div>
          {/* Only show file list here in non-iconOnly mode */}
          {!iconOnly && (
            <div className='bottom-attchement-box'>
              {existingFiles.length > 0 && (
                <Box className={classes.fileList}>
                  {existingFiles.map((file) => (
                    <Box key={file['id']} className={classes.fileItem}>
                      {getFileIcon(file['type'] || file['mimeType'])}
                      <Typography variant="body2" className={classes.fileName} noWrap>
                        {file['name'] || file['fileName']}
                      </Typography>
                      <Typography variant="caption" className={classes.fileSize}>
                        {formatFileSize(file['size'] || file['fileSize'])}
                      </Typography>
                      {uploadProgress[file['id']] !== undefined && (
                        <Box width={100}>
                          <LinearProgress variant="determinate" value={uploadProgress[file['id']]} />
                        </Box>
                      )}
                      {!uploadProgress[file['id']] && (
                        <IconButton size="small" onClick={() => handleRemoveFile(file['id'])}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </div>
          )}
        </>
      )}

      {/* --------------- Altert Dropped--------------- */}
      <Modal isOpen={modalDroppedAlert} toggle={() => setModalDroppedAlert(false)} className={`main-modal new-user-modal modal-dialog-centered`}>
        <ModalHeader toggle={() => setModalDroppedAlert(false)}></ModalHeader>
        <ModalBody>
          <div className='add-content'>
            <h2>Some files could not be added: {invalidFilesList.map((file, i) => (
              <li key={i}>{file}</li>
            ))}</h2>
            <div className='templates-inner'>
              <div className='btn-group'>
                <button className='btn-style-new' onClick={() => setModalDroppedAlert(false)}>Ok</button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* ---------- Max Files Limit Modal ---------- */}
      <Modal
        isOpen={maxFilesAlert}
        toggle={() => setMaxFilesAlert(false)}
        className="main-modal new-user-modal modal-dialog-centered"
      >
        <ModalHeader toggle={() => setMaxFilesAlert(false)}>
          File Limit Reached
        </ModalHeader>
        <ModalBody>
          <div className='add-content text-center mt-4'>
            <p>{maxFilesMessage}</p>
            {/* centered the button */}
            <div className={`templates-inner mt-3 ${classes.buttonContainer}`}>
              {/*  Position button in bottom-right corner */}
              <button className='btn-style-new' onClick={() => setMaxFilesAlert(false)}>OK</button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default AttachmentUploader;